
  import OrderWorklogsCreateFab from "@/views/Orders/Worklogs/OrderWorklogsCreateFab.vue";
  import OrderWorklogsList from "@/views/Orders/Worklogs/OrderWorklogsList.vue";
  import OrderWorklogsTable from "@/views/Orders/Worklogs/OrderWorklogsTable.vue";
  import SwitchView from "@/components/SwitchView.vue";
  import {computed, defineComponent, onBeforeUnmount, onMounted, PropType, reactive, toRefs, watch} from "vue";
  import {groupOrderWorklogs, OrderWorklogsDate} from "@/model/OrderWorklogs";
  import MonthPicker from "@/components/MonthPicker.vue";
  import {MessageTypes} from "@/components/message-types";
  import OrderServiceV2 from "@/service/OrderServiceV2";
  import eventBus from "@/eventbus";
  import {t} from "@/mixin/mixins";
  import Worklog from "@/model/Worklog";
  import WorklogService from "@/service/WorklogService";
  import {orderId} from "@/RouterUtils";
  import router from "@/router";
  import Order from "@/model/Order";
  import store from "@/store/store";
  import moment from "moment";

  export default defineComponent({
    name: "OrderWorklogs",
    components: {
      OrderWorklogsCreateFab,
      MonthPicker,
      OrderWorklogsList,
      OrderWorklogsTable,
      SwitchView
    },
    props: {
      order: {
        type: Object as PropType<Order>,
        default: () => null,
      }
    },
    setup (props, context) {
      const initialState = {
        worklogs: [] as Array<OrderWorklogsDate>,
        loading: false,
        yearMonth: null as unknown as string,
        orderUuid: null as string | null
      };

      const state = reactive({...initialState});

      const date = computed(() => {
        return router.currentRoute.query.date ? router.currentRoute.query.date as string : null;
      });

      const isDate = computed(() => {
        return moment(date.value as string, "YYYY-MM-DD", true).isValid();
      });

      const breadcrumbs = computed(() => {
        const breadcrumbs = [];
        breadcrumbs.push({
          text: moment(state.yearMonth as string, "YYYY-MM").format("MMMM YYYY"),
          disabled: !isDate.value,
          exact: true,
          to: {path: router.currentRoute.path, query: {view: "list", date: state.yearMonth}}
        });
        if (isDate.value) {
          breadcrumbs.push({
            text: moment(date.value as string).format("DD. MMMM YYYY"),
            disabled: true
          });
        }

        return breadcrumbs;
      });

      const min = computed(() => {
        return props.order && props.order.validFrom ? moment(props.order.validFrom).format("YYYY-MM") : "";
      });

      const max = computed(() => {
        return props.order && props.order.validTo ? moment(props.order.validTo).format("YYYY-MM") : "";
      });

      watch(() => state.yearMonth, (newValue, oldValue) => {
        // oldValue is initially not set and the first month is loaded by loadOrder, so do not load it here again
        if(oldValue != null && oldValue !== "") {
          loadWorklogs();
        }
        // A day of the month stays in the route, so that reloading the page opens the same day
        if (!date.value || !date.value.startsWith(newValue)) {
          router.push({query: Object.assign({}, router.currentRoute.query, {date: newValue}), replace: true});
        }
      });

      // The worklogs are read by the order's uuid, which the numeric id in the route does not give
      const loadOrder = () => {
        OrderServiceV2.getOrder(orderId.value)
          .then(response => {
            state.orderUuid = response.data.uuid ?? null;
            state.yearMonth = initialMonth(response.data);
            loadWorklogs();
          });
      };

      // The month of the route's date, else the current month moved into the order's validity period
      const initialMonth = (order: Order): string => {
        const routeMonth = date.value ? date.value.slice(0, 7) : null;
        if (routeMonth && moment(routeMonth, "YYYY-MM", true).isValid()) {
          return routeMonth;
        }
        const now = moment().format("YYYY-MM");
        const first = order.validFrom ? moment(order.validFrom).format("YYYY-MM") : now;
        const last = order.validTo ? moment(order.validTo).format("YYYY-MM") : now;
        return now < first ? first : (now > last ? last : now);
      };

      const loadWorklogs = () => {
        if (!state.orderUuid) {
          return;
        }
        const month = state.yearMonth;
        state.loading = true;

        OrderServiceV2.findOrderWorklogsV3(state.orderUuid, month)
          .then(worklogs => {
            // A month picked meanwhile has loaded or is loading its own worklogs
            if (month === state.yearMonth) {
              state.worklogs = groupOrderWorklogs(worklogs);
            }
          })
          .finally(() => state.loading = false);
      };

      onMounted(() => {
        store.commit("header/setTitle", props.order.name);

        loadOrder();
        eventBus.$on(MessageTypes.DELETE_WORKLOG, (worklog: Worklog) => {
          WorklogService.delete(worklog)
            .then((deleted: boolean) => {
              if (deleted) {
                eventBus.$emit(MessageTypes.SNACKBAR_SUCCESS, t("entryDeleted"));
                eventBus.$emit(MessageTypes.WORKLOG_DELETED);
              }
            });
        });

        eventBus.$on(MessageTypes.WORKLOG_CREATED, () => {
          loadWorklogs();
        });
        eventBus.$on(MessageTypes.WORKLOG_DELETED, () => {
          loadWorklogs();
        });
        eventBus.$on(MessageTypes.WORKLOG_UPDATED, () => {
          loadWorklogs();
        });
      });

      onBeforeUnmount(() => {
        eventBus.$off(MessageTypes.WORKLOG_CREATED);
        eventBus.$off(MessageTypes.WORKLOG_UPDATED);
        eventBus.$off(MessageTypes.WORKLOG_DELETED);
        eventBus.$off(MessageTypes.DELETE_WORKLOG);
      });

      return {
        ...toRefs(state),
        breadcrumbs,
        date,
        isDate,
        min,
        max
      };
    }
  });
